import Vue from 'vue';
import chunk from 'lodash/chunk';
import EventBus from '@/helpers/EventBus.js';

export const namespaced = true;

export const state = {
  currentUserProfileFetched: false,
  roles: [
    {
      id: 1,
      name: 'Tie',
      homePage: 'TamaiHome',
      homeItems: [
        'corporation',
        'brand',
        'school',
        'course',
        'teacher',
        'class',
        'student',
        'information',
        'experience',
        'frozen',
      ],
      homeRowSize: 4,
      idTypeTitle: 'TIE',
      sidebarItems: ['home', 'notice'],
      inferiorControlItems: ['corporation', 'brand', 'school', 'teacher', 'student'],
    },
    {
      id: 2,
      name: 'Corporation',
      homePage: 'CorporationHome',
      homeItems: ['corporation', 'brand', 'school', 'course', 'teacher', 'class', 'student'],
      homeRowSize: 4,
      idTypeTitle: '法人',
      sidebarItems: ['home', 'contact'],
      inferiorControlItems: ['brand', 'school', 'teacher', 'student'],
    },
    {
      id: 3,
      name: 'Brand',
      homePage: 'BrandHome',
      homeItems: ['brand', 'school', 'course', 'teacher', 'class', 'student'],
      homeRowSize: 4,
      idTypeTitle: 'ブランド',
      sidebarItems: ['home', 'contact'],
      inferiorControlItems: ['school', 'teacher', 'student'],
    },
    {
      id: 4,
      name: 'School',
      homePage: 'SchoolHome',
      homeItems: ['school', 'class', 'student'],
      homeRowSize: 4,
      idTypeTitle: '教場ID',
      sidebarItems: ['home', 'contact'],
      inferiorControlItems: ['teacher', 'student'],
    },
    {
      id: 5,
      name: 'Teacher',
      homePage: 'TeacherHome',
      homeItems: [],
      homeRowSize: 0,
      idTypeTitle: '先生',
      sidebarItems: ['lesson', 'student', 'advance', 'preparation', 'teacher-notice', 'contact', 'teacher-setting'],
      inferiorControlItems: ['student'],
    },
  ],
  currentRole: {
    id: 3,
    name: 'Brand',
    homePage: 'BrandHome',
    homeItems: ['brand', 'school', 'course', 'teacher', 'class', 'student'],
    homeRowSize: 4,
    idTypeTitle: 'ブランド',
    sidebarItems: ['home', 'contact'],
    inferiorControlItems: ['school', 'teacher'],
  },
};

export const mutations = {
  SET_CURRENT_ROLE(state, currentRole) {
    state.currentRole = currentRole;
  },
  SET_CURRENT_USER_PROFILE_FETCHED(state) {
    state.currentUserProfileFetched = true;
  },
};

export const actions = {
  fetchCurrentRole({ commit, getters }, name) {
    const currentRole = getters.getRoleByName(name);
    commit('SET_CURRENT_ROLE', currentRole);
  },
  fetchUserProfile({ commit, dispatch }, payload) {
    switch (payload.currentRole) {
      case 'Corporation':
        dispatch('corporation/fetchCorporation', payload.currentProfileId, { root: true }).then((response) => {
          Vue.$logger.debug('[APP] CurrentUserProfile restored:', response);
          commit('SET_CURRENT_USER_PROFILE_FETCHED');
          dispatch('corporation/setCurrentCorporationUser', response, { root: true });
          EventBus.$emit('SET_USER_PROFILE', { data: response });
        });
        break;
      case 'Brand':
        dispatch('brand/fetchBrand', payload.currentProfileId, { root: true }).then((response) => {
          Vue.$logger.debug('[APP] CurrentUserProfile restored:', response);
          commit('SET_CURRENT_USER_PROFILE_FETCHED');
          dispatch('brand/setCurrentBrandUser', response, { root: true });
          EventBus.$emit('SET_USER_PROFILE', { data: response });
        });
        break;
      case 'School':
        dispatch('school/fetchSchool', payload.currentProfileId, { root: true }).then((response) => {
          Vue.$logger.debug('[APP] CurrentUserProfile restored:', response);
          commit('SET_CURRENT_USER_PROFILE_FETCHED');
          dispatch('school/setCurrentSchoolUser', response, { root: true });
          EventBus.$emit('SET_USER_PROFILE', { data: response });
        });
        break;
      case 'Teacher':
        dispatch('teacher/fetchTeacher', payload.currentProfileId, { root: true }).then((response) => {
          Vue.$logger.debug('[APP] CurrentUserProfile restored:', response);
          commit('SET_CURRENT_USER_PROFILE_FETCHED');
          dispatch('teacher/setCurrentTeacherUser', response, { root: true }).then(() => {
            dispatch('teacher/fetchTamaiClasses', response, { root: true });
          });
          EventBus.$emit('SET_USER_PROFILE', { data: response });
        });
        break;
      default:
        // For TIE role, mark app was fetched current user data
        commit('SET_CURRENT_USER_PROFILE_FETCHED');
        break;
    }
  },
};

export const getters = {
  anyHomeItem() {
    return state.currentRole.homeItems.length > 0;
  },
  groupItems() {
    return chunk(state.currentRole.homeItems, state.currentRole.homeRowSize);
  },
  getRoleByName: (state) => (name) => {
    return state.roles.find((item) => item.name === name);
  },
  allowEditingCorporation: (state, getters) => {
    return state.currentRole.inferiorControlItems.includes('corporation') && getters.hasAdminPrivilege;
  },
  allowEditingBrand: (state, getters) => {
    return state.currentRole.inferiorControlItems.includes('brand') && getters.hasAdminPrivilege;
  },
  allowEditingSchool: (state, getters) => {
    return state.currentRole.inferiorControlItems.includes('school') && getters.hasAdminPrivilege;
  },
  allowEditingTeacher: (state) => {
    return state.currentRole.inferiorControlItems.includes('school');
  },
  allowEditingStudent: (state) => {
    return state.currentRole.inferiorControlItems.includes('student');
  },
  allowDeletingVideoViewing: (state, getters) => {
    return state.currentRole.inferiorControlItems.includes('school') && getters.hasAdminPrivilege;
  },
  hasAdminPrivilege: (state) => {
    return state.currentRole.name === 'Tie';
  },
  hasTeacherPrivilege: (state) => {
    return state.currentRole.name === 'Teacher';
  },
  hasCorporationPrivilege: (state) => {
    return state.currentRole.name === 'Corporation';
  },
  hasBrandPrivilege: (state) => {
    return state.currentRole.name === 'Brand';
  },
  hasSchoolPrivilege: (state) => {
    return state.currentRole.name === 'School';
  },
  currentUserProfileFetched: (state) => {
    return state.currentUserProfileFetched;
  },
};
